import { TableRowsRounded, ViewModuleRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Grid,
  Skeleton,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { AI_APP_STORE_LABEL } from '../../constants/promptLabels';
import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { useGetElementSize } from '../../hooks/useGetElementSize';
import { StorageKeys, useLocalStorageState } from '../../hooks/useLocalStorageState';
import { usePromptDrawer } from '../../hooks/usePromptDrawer';
import { findPrompts } from '../../redux/actions/promptActions';
import { myFolder } from '../../redux/consts';
import { selectFolders } from '../../redux/reducers/folderReducer';
import {
  selectPrompts,
  setArchivedOnly,
  setExcludedLabels,
  setFolderPinned,
  setLimit,
  setMyPromptsOnly,
  setPage,
  setPinned,
  setPromptView,
  setPublishedOnly,
  setRecent,
  setSearchQuery,
  setSelectedLabels,
  setSortDirection,
  setSortOption,
} from '../../redux/reducers/promptReducer';
import { AppDispatch } from '../../redux/store';
import { StyledTab, StyledTabs } from '../../theme/CustomComponents';
import {
  PromptSortOptions,
  PromptView,
  PromptViewConfig,
  PromptViewToUrlMap,
  SortDirection,
  UrlToPromptViewMap,
} from '../../Types/enums';
import { Prompt } from '../../Types/prompt';
import { useIsMobile } from '../../util/useIsMobile';
import DebouncedSearchInput from '../DebouncedSearchInput';
import PromptFilters from '../Prompts/PromptFilters';
import PromptGridSortDropdown from '../Prompts/PromptGridSortDropdown';
import PromptsGrid from '../Prompts/PromptsGrid';
import PromptsTable from '../Prompts/PromptsTable';
import { useAuth } from '../Providers/AuthProvider';

const SearchContainer = styled('div')<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  justifyContent: 'space-between',
  gap: '30px',
  paddingBottom: '20px',
}));

const PromptsContainer = styled('div')({
  display: 'flex',
  width: '100%',
});

const ToggleButtonContainer = styled(Box)({
  display: 'flex',
  width: '100%',
  minHeight: '40px',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  marginTop: '15px',
  marginBottom: '20px',
});

enum ViewMode {
  LIST = 'LIST',
  TABLE = 'TABLE',
}

export interface PromptsProps {
  isAIAppStore: boolean;
}

const Prompts = ({ isAIAppStore }: PromptsProps): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useClassicNavigate();
  const { currentUser, isAdmin } = useAuth();
  const { view } = useParams();
  const isMobile = useIsMobile();
  const {
    isLoading,
    searchQuery,
    page,
    promptView,
    publishedOnly,
    myPromptsOnly,
    sortOption,
    sortDirection,
    selectedLabels,
    excludedLabels,
    archivedOnly,
    recentOnly,
    pinned,
    folderPinned,
    limit,
  } = useSelector(selectPrompts);
  const { currentFolder } = useSelector(selectFolders);

  const [newlyCreatedId, setNewlyCreatedId] = useState<string | null>(null);
  const { height: promptTabsHeight } = useGetElementSize('prompt-tabs');
  const [viewMode, setViewMode] = useLocalStorageState<ViewMode>(
    StorageKeys.PROMPTS_VIEW_MODE,
    ViewMode.LIST
  );

  const onNewPrompt = useCallback(
    (newPrompt: Prompt) => {
      const nextPromptView = isAIAppStore ? PromptView.ALL_PROMPTS : PromptView.MY_PROMPTS;
      dispatch(setPromptView(nextPromptView));
      dispatch(setPage(1));
      setNewlyCreatedId(newPrompt.id);

      if (!isAIAppStore) {
        const viewPath = PromptViewToUrlMap[nextPromptView];
        navigate(`/folder/${currentFolder?.id ?? myFolder.id}/prompts/${viewPath}`);
      }
    },
    [dispatch, isAIAppStore, navigate, currentFolder?.id]
  );

  const onPromptsViewChange = useCallback(
    (value: ViewMode) => {
      if (value) {
        setViewMode(value);

        // Reset Redux sort/pagination state when switching from grid to table and vice versa
        dispatch(setSortOption(PromptSortOptions.DEFAULT));
        dispatch(setSortDirection(SortDirection.ASC));
        dispatch(setPage(1));
        dispatch(setLimit(10));
      }
    },
    [dispatch, setViewMode]
  );

  const onEditPrompt = useCallback(() => {
    dispatch(findPrompts());
  }, [dispatch]);

  // Open the prompt drawer if a promptId is provided in the URL
  const { openPromptDrawer } = usePromptDrawer({
    promptView,
    onNewPrompt,
    onEditPrompt,
    isAIAppStore,
    autoOpenFromUrl: true,
  });

  const onButtonForm = useCallback(() => {
    if (isAIAppStore && !isAdmin) {
      window.open(
        'https://forms.office.com/Pages/ResponsePage.aspx?id=5Tuiws_ScE6Ezhit-H1XH6pgX0pA5plFuPB8xlSQJBxURTk4NTlQUlFYMVZWMTVNVkNEVlhPSTlPWC4u',
        '_blank'
      );
    } else {
      openPromptDrawer();
    }
  }, [isAIAppStore, isAdmin, openPromptDrawer]);

  // PromptViewConfig useEffect
  useEffect(() => {
    const config = PromptViewConfig[promptView];
    dispatch(setPublishedOnly(config.publishedOnly));
    dispatch(setRecent(config.recent));
    dispatch(setPinned(config.pinned));
    dispatch(setFolderPinned(config.folderPinned));
    dispatch(setArchivedOnly(config.archivedOnly));
    dispatch(setMyPromptsOnly(config.myPromptsOnly));
  }, [promptView, dispatch]);

  //Tags useEffect
  useEffect(() => {
    const hasAIStoreTag = selectedLabels.includes(AI_APP_STORE_LABEL);

    if (isAIAppStore && !hasAIStoreTag) {
      dispatch(setSelectedLabels([...selectedLabels, AI_APP_STORE_LABEL]));
    }

    if (!isAIAppStore && hasAIStoreTag) {
      dispatch(
        setSelectedLabels(selectedLabels.filter((label: string) => label !== AI_APP_STORE_LABEL))
      );
    }

    const excluded = isAIAppStore ? [] : [AI_APP_STORE_LABEL];
    dispatch(setExcludedLabels(excluded));
  }, [promptView, isAIAppStore, selectedLabels, dispatch]);

  // Sync prompt view with isAIAppStore prop
  useEffect(() => {
    if (isAIAppStore) {
      dispatch(setPromptView(PromptView.ALL_PROMPTS));
    } else if (view && view in UrlToPromptViewMap) {
      dispatch(setPromptView(UrlToPromptViewMap[view as keyof typeof UrlToPromptViewMap]));
    } else {
      // Backwards compatibility: If there is no view, default to the all prompts view
      dispatch(setPromptView(PromptView.ALL_PROMPTS));
    }
  }, [isAIAppStore, dispatch, view]);

  // Clear the filters when the components unmounts so the state does not stay
  // dirty for other components that use these filters like the library drawer
  useEffect(
    () => () => {
      dispatch(setSearchQuery(''));
      dispatch(setSelectedLabels([]));
      dispatch(setPage(1));
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(findPrompts());
  }, [
    currentUser?.id,
    publishedOnly,
    myPromptsOnly,
    page,
    sortOption,
    searchQuery,
    sortDirection,
    selectedLabels,
    excludedLabels,
    archivedOnly,
    recentOnly,
    pinned,
    folderPinned,
    currentFolder?.id,
    limit,
    dispatch,
  ]);

  const clearNewlyCreated = useCallback(() => setNewlyCreatedId(null), []);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        overflowY: 'auto',
        height: '100%',
        marginRight: '20px',
      }}
    >
      <Container
        sx={{ bgcolor: 'white', height: 'fit-content', margin: '0px' }}
        style={{ padding: '0px' }}
      >
        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ width: 'fit-content', height: `calc(100% - 64px)` }}>
            <div id="prompt-tabs">
              {!isAIAppStore && (
                <StyledTabs
                  value={promptView}
                  onChange={(event: React.SyntheticEvent, newView: PromptView) => {
                    const viewPath = PromptViewToUrlMap[newView];
                    dispatch(setPromptView(newView));
                    navigate(`/folder/${currentFolder?.id ?? myFolder.id}/prompts/${viewPath}`);
                  }}
                >
                  {currentFolder && currentFolder.id !== myFolder.id && (
                    <StyledTab disableRipple value={PromptView.PINNED} label="Pinned To Folder" />
                  )}
                  <StyledTab disableRipple value={PromptView.ALL_PROMPTS} label="All Prompts" />
                  <StyledTab disableRipple value={PromptView.SHARED} label="Shared With Me" />
                  <StyledTab disableRipple value={PromptView.RECENT} label="Recently Used" />
                  <StyledTab disableRipple value={PromptView.MY_PINNED} label="My Pinned" />
                  <StyledTab disableRipple value={PromptView.MY_PROMPTS} label="Owned By Me" />
                  <StyledTab disableRipple value={PromptView.ARCHIVED} label="Archived" />
                </StyledTabs>
              )}
              <ToggleButtonContainer>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, value) => onPromptsViewChange(value)}
                  aria-label="view mode"
                  size="small"
                  sx={{
                    position: 'absolute !important',
                    right: 0,
                    '& .MuiToggleButton-root': {
                      padding: '4px',
                      minWidth: '32px',
                      minHeight: '32px',
                    },
                  }}
                >
                  <ToggleButton value={ViewMode.LIST} sx={{ padding: 0 }}>
                    <Tooltip title="List view" arrow>
                      <TableRowsRounded fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value={ViewMode.TABLE} sx={{ padding: 0 }}>
                    <Tooltip title="Table view" arrow>
                      <ViewModuleRounded fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </ToggleButtonContainer>
              <SearchContainer isMobile={isMobile}>
                <DebouncedSearchInput
                  value={searchQuery}
                  valueUpdated={(value) => {
                    dispatch(setSearchQuery(value));
                  }}
                />
                {viewMode === ViewMode.LIST && (
                  <PromptGridSortDropdown sortOption={sortOption} sortDirection={sortDirection} />
                )}
              </SearchContainer>
            </div>
            <br />
            <Box
              style={{
                overflowY: 'auto',
                height: `calc(100vh - 130px - ${
                  promptTabsHeight + 48
                }px - var(--banner-height, 0px))`,
                width: '900px', // Fixed width for consistent rendering between views
              }}
            >
              {isLoading ? (
                <Grid container columnSpacing={-10} rowSpacing={2} sx={{ width: '100% ' }}>
                  {Array(6)
                    .fill(null)
                    .map((_, index) => (
                      <Grid key={index} item xs={12}>
                        <Skeleton
                          variant="rounded"
                          width="100%"
                          height="101px"
                          animation="wave"
                          style={{
                            borderRadius: '20px',
                            backgroundColor: '#F3F6F4',
                          }}
                        />
                      </Grid>
                    ))}
                </Grid>
              ) : (
                <PromptsContainer>
                  {viewMode === ViewMode.LIST ? (
                    <PromptsGrid
                      isAIAppStore={isAIAppStore}
                      newlyCreatedId={newlyCreatedId}
                      clearNewlyCreated={clearNewlyCreated}
                    />
                  ) : (
                    <PromptsTable isAIAppStore={isAIAppStore} />
                  )}
                </PromptsContainer>
              )}
            </Box>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Button
              sx={{ marginBottom: '5px', display: 'inline-block', marginRight: '20px' }}
              variant="contained"
              onClick={onButtonForm}
            >
              {isAIAppStore ? 'Submit App' : 'Create Prompt'}
            </Button>
            <PromptFilters isAIAppStore={isAIAppStore} />
          </div>
        </div>
      </Container>
    </div>
  );
};
export default Prompts;
